// Per-family recovery routes for RCRA code pages — the "intent bridge"
// between "what is this code" and "where can this stream go" (Phase 2,
// docs/plans/2026-07-08-code-page-conversion-layer.md).
//
// Every route traces to a regulatory recovery pathway — no fabricated
// routes, no per-code hand-written content, and no dollar values (free
// surfaces are recovery-tier only per product copy rules).
//
// @source 40 CFR 261.1(c)(4) — definition of reclamation
// @source 40 CFR 261.2(c)(3) Table 1 — commercial chemical products (261.33) being reclaimed are not solid wastes
// @source 40 CFR 261.4(a)(8) — closed-loop reclamation exclusion
// @source 40 CFR 261.4(a)(12) — oil-bearing residues reinserted into petroleum refining excluded from solid waste
// @source 40 CFR Part 266 Subpart F — recyclable materials utilized for precious metal recovery
// @source 40 CFR Part 266 Subpart G — spent lead-acid batteries being reclaimed
// @source 40 CFR Part 266 Subpart H — hazardous waste burned in boilers and industrial furnaces
// @source 40 CFR 268.40 — land disposal restrictions treatment standards
// @source 40 CFR 268.42 Table 1 — technology codes incl. HTMR (high temperature metals recovery)
// @source 40 CFR 260.10 — definitions: elementary neutralization unit, carbon regeneration unit
import type { RcraEntry } from './rcraCatalogue';
import { route, FEDERAL_SOURCES as SRC } from './recoveryRouteTypes';
import type { RecoveryRoute } from './recoveryRouteTypes';

export type { RecoveryRoute, RecoveryTier } from './recoveryRouteTypes';

// --- Shared routes -----------------------------------------------------

const ENERGY_RECOVERY = route(
  'Fuel blending & energy recovery',
  'Medium',
  'Organic-bearing streams with usable fuel value can be blended and burned for energy recovery in permitted boilers and industrial furnaces instead of being incinerated as pure disposal.',
  SRC.energyRecovery,
);

const LDR_TREATMENT = route(
  'Treatment to LDR standard, then disposal',
  'Low',
  'Where no recovery route fits the stream, land disposal restriction treatment standards define the required treatment before landfill — the compliance floor, not a recovery outcome.',
  SRC.ldr,
);

const IN_PROCESS_RECLAMATION = route(
  'In-process reclamation',
  'Medium',
  'Process residues that still carry recoverable product or feedstock value can be reclaimed — processed to recover a usable material or regenerated for reuse — inside or outside the generating industry.',
  SRC.reclamation,
);

const METALS_RECOVERY_HTMR = route(
  'Metals recovery (HTMR / hydrometallurgical)',
  'Medium',
  'Metal-bearing residues route to high temperature metals recovery or hydrometallurgical processing, where the contained metals are extracted and returned to commerce as secondary raw material.',
  SRC.htmr,
);

const STABILIZATION = route(
  'Stabilization & controlled disposal',
  'Low',
  'Below economic metal concentrations, stabilization to the land disposal restriction treatment standard followed by controlled disposal remains the default management route.',
  SRC.ldr,
);

// --- Family route sets -------------------------------------------------

const D_IGNITABLE: RecoveryRoute[] = [
  route(
    'Solvent & organics reclamation',
    'High',
    'Solvent-bearing ignitable liquids are prime reclamation candidates: distillation recovers a reusable solvent fraction and removes the ignitability characteristic from the residue in one step.',
    SRC.reclamation,
  ),
  ENERGY_RECOVERY,
  route(
    'Deactivation of the characteristic',
    'Low',
    'Treatment that removes the ignitability characteristic (deactivation) can take the stream out of hazardous management entirely, opening conventional recovery or disposal routes.',
    SRC.ldr,
  ),
];

const D_CORROSIVE: RecoveryRoute[] = [
  route(
    'Acid / alkali regeneration',
    'Medium',
    'Spent acids and caustics are regeneration candidates: spent pickling and etching liquors can be processed for acid recovery or metal-salt by-products, depending on free-acid strength and dissolved metal load.',
    SRC.reclamation,
  ),
  route(
    'On-site elementary neutralization',
    'Low',
    'Wastes hazardous only for corrosivity can be treated in an elementary neutralization unit — a recognized unit type that manages the characteristic without a full treatment permit.',
    SRC.definitions,
  ),
];

const D_REACTIVE: RecoveryRoute[] = [
  route(
    'Deactivation treatment',
    'Specialist',
    'Reactive wastes carry a deactivation treatment standard: the reactivity must be removed under controlled conditions by specialist processors before any downstream management.',
    SRC.ldr,
  ),
  route(
    'Cyanide destruction with metals recovery',
    'Specialist',
    'Cyanide-bearing reactive streams from plating and heat-treating are treated by specialist processors for cyanide destruction; metal-rich residues can continue to metals reclamation.',
    SRC.reclamation,
  ),
];

const D_METALS_BASE: RecoveryRoute[] = [METALS_RECOVERY_HTMR, STABILIZATION];

const D_PESTICIDE: RecoveryRoute[] = [
  route(
    'High-temperature combustion',
    'Specialist',
    'Pesticide-bearing characteristic wastes carry combustion-based treatment standards; destruction at permitted high-temperature units is the established route for the organic load.',
    SRC.ldr,
  ),
  route(
    'Energy recovery where fuel value exists',
    'Low',
    'Streams with meaningful organic fuel content may qualify for burning for energy recovery in permitted industrial furnaces rather than pure destruction — dependent on halogen and metals limits.',
    SRC.energyRecovery,
  ),
];

const D_ORGANIC: RecoveryRoute[] = [
  ENERGY_RECOVERY,
  route(
    'Solvent-fraction reclamation',
    'Medium',
    'Where the toxicity-characteristic constituent is itself a recoverable solvent (benzene, MEK, pyridine and similar), distillation can recover a usable fraction and shrink the hazardous residue.',
    SRC.reclamation,
  ),
  route(
    'Spent activated carbon regeneration',
    'Medium',
    'Spent activated carbon loaded with organic constituents routes to carbon regeneration units, restoring the carbon for reuse instead of single-pass disposal.',
    SRC.definitions,
  ),
];

const F_SOLVENT: RecoveryRoute[] = [
  route(
    'Solvent distillation & reuse',
    'High',
    'Spent solvents are among the most recycled RCRA streams: distillation recovers a technical-grade solvent for reuse, with only the still bottoms remaining as waste.',
    SRC.reclamation,
  ),
  route(
    'Closed-loop recovery at the point of generation',
    'High',
    'Solvent reclaimed in an enclosed, hard-piped loop back into the generating process can be excluded from waste regulation entirely — the strongest position a solvent stream can be in.',
    SRC.closedLoop,
  ),
  route(
    'Fuel blending of still bottoms',
    'Medium',
    'Distillation residues and unrecoverable solvent fractions retain fuel value and route to fuel blending for energy recovery in permitted industrial furnaces.',
    SRC.energyRecovery,
  ),
];

const F_METAL_FINISHING: RecoveryRoute[] = [
  route(
    'Metals recovery (HTMR / hydrometallurgical)',
    'Medium',
    'Electroplating and metal-finishing residues carry recoverable nickel, copper, zinc and cadmium; high temperature metals recovery and hydrometallurgical processing are the established routes.',
    SRC.htmr,
  ),
  IN_PROCESS_RECLAMATION,
  STABILIZATION,
];

const F_DIOXIN: RecoveryRoute[] = [
  route(
    'High-temperature combustion',
    'Specialist',
    'Dioxin-bearing wastes carry combustion-based treatment standards at the most tightly controlled permitted units; no material recovery route is recognized for the dioxin fraction.',
    SRC.ldr,
  ),
  route(
    'Containment & specialist disposal',
    'Specialist',
    'Where combustion capacity is unavailable, secure containment under the land disposal restriction regime is the only compliant management — a pure liability stream.',
    SRC.ldr,
  ),
];

const F_CHLORINATED: RecoveryRoute[] = [
  IN_PROCESS_RECLAMATION,
  route(
    'Controlled combustion',
    'Specialist',
    'Chlorinated production residues route to combustion-based treatment at permitted units equipped for halogenated streams; chlorine content constrains energy-recovery options.',
    SRC.ldr,
  ),
];

const F_WOOD_PRESERVING: RecoveryRoute[] = [
  route(
    'Preservative recovery & reuse in-process',
    'Medium',
    'Wood-preserving process residues can be reclaimed for preservative recovery back into the treating process where formulation and contamination allow.',
    SRC.reclamation,
  ),
  ENERGY_RECOVERY,
  STABILIZATION,
];

const F_REFINERY_SLUDGE: RecoveryRoute[] = [
  route(
    'Oil recovery via reinsertion into refining',
    'High',
    'Oil-bearing separation sludges returned to the petroleum refining process for oil recovery are excluded from solid waste regulation — the highest-value route for these streams.',
    SRC.oilReinsertion,
  ),
  ENERGY_RECOVERY,
];

const F_LEACHATE: RecoveryRoute[] = [
  route(
    'Wastewater treatment & water reuse',
    'Low',
    'Multi-source leachate is a high-volume, low-concentration stream: recovery is limited to treated-water reuse; the concentrated residue follows the land disposal restriction standards.',
    SRC.ldr,
  ),
  route(
    'Specialist constituent review',
    'Specialist',
    'Leachate quality varies by source cell; a constituent-level review can reveal recoverable fractions or cheaper compliant treatment trains than the default assumption.',
    SRC.reclamation,
  ),
];

const K_PETROLEUM: RecoveryRoute[] = [
  route(
    'Oil recovery via reinsertion into refining',
    'High',
    'Oil-bearing refinery residues reinserted into the refining process for oil recovery are excluded from solid waste regulation — the recognized highest-value route for these streams.',
    SRC.oilReinsertion,
  ),
  ENERGY_RECOVERY,
  LDR_TREATMENT,
];

const K_METALS: RecoveryRoute[] = [
  route(
    'High temperature metals recovery',
    'Medium',
    'Metal-bearing industry residues — furnace dusts, pickle liquors, smelting residues — route to high temperature metals recovery, where zinc, lead and other metals return to commerce.',
    SRC.htmr,
  ),
  IN_PROCESS_RECLAMATION,
  STABILIZATION,
];

const K_GENERIC: RecoveryRoute[] = [
  IN_PROCESS_RECLAMATION,
  ENERGY_RECOVERY,
  LDR_TREATMENT,
];

const PU_DISCARDED: RecoveryRoute[] = [
  route(
    'Return to supplier or redistribution',
    'High',
    'Unused, on-spec chemical product that finds a user has never been discarded — redistribution, resale or return to the supplier keeps it out of the waste system entirely.',
    SRC.ccpReclaim,
  ),
  route(
    'Reclamation of the chemical product',
    'High',
    'Commercial chemical products being reclaimed are not solid wastes: off-spec, expired or surplus inventory processed back to usable product exits hazardous waste regulation.',
    SRC.ccpReclaim,
  ),
];

// --- Per-contaminant additions (D-list TC metals) ----------------------

const LEAD_BATTERY = route(
  'Lead-acid battery reclamation',
  'High',
  'Where the lead stream is spent lead-acid batteries, the dedicated battery reclamation regime applies — secondary lead smelting is one of the most established recovery chains in hazardous waste.',
  SRC.leadAcid,
);

const SILVER_RECOVERY = route(
  'Precious metal recovery',
  'High',
  'Silver-bearing wastes qualify for the reduced-burden precious metal recovery regime — photographic and plating silver streams are routinely refined back to metal.',
  SRC.preciousMetals,
);

// --- Resolution --------------------------------------------------------

const K_PETROLEUM_CODES = new Set(['K048', 'K049', 'K050', 'K051', 'K052', 'K169', 'K170', 'K171', 'K172']);
const K_METALS_CODES = new Set(['K061', 'K062', 'K069', 'K071', 'K088', 'K100', 'K106']);
const F_DIOXIN_CODES = new Set(['F020', 'F021', 'F022', 'F023', 'F026', 'F027', 'F028']);
const F_WOOD_CODES = new Set(['F032', 'F034', 'F035']);
const F_REFINERY_CODES = new Set(['F037', 'F038']);

export function getRcraRecoveryRoutes(entry: { code: string; list: string; contaminant?: string }): RecoveryRoute[] {
  const { code, list } = entry;
  const num = parseInt(code.slice(1), 10);

  if (list === 'D') {
    if (code === 'D001') return D_IGNITABLE;
    if (code === 'D002') return D_CORROSIVE;
    if (code === 'D003') return D_REACTIVE;
    if (num >= 4 && num <= 11) {
      if (entry.contaminant === 'Lead') return [LEAD_BATTERY, ...D_METALS_BASE];
      if (entry.contaminant === 'Silver') return [SILVER_RECOVERY, ...D_METALS_BASE];
      return D_METALS_BASE;
    }
    if (num >= 12 && num <= 17) return D_PESTICIDE;
    return D_ORGANIC; // D018-D043
  }

  if (list === 'F') {
    if (num >= 1 && num <= 5) return F_SOLVENT;
    if (F_DIOXIN_CODES.has(code)) return F_DIOXIN;
    if (code === 'F024' || code === 'F025') return F_CHLORINATED;
    if (F_WOOD_CODES.has(code)) return F_WOOD_PRESERVING;
    if (F_REFINERY_CODES.has(code)) return F_REFINERY_SLUDGE;
    if (code === 'F039') return F_LEACHATE;
    return F_METAL_FINISHING; // F006-F012, F019 metal finishing / quenching
  }

  if (list === 'K') {
    if (K_PETROLEUM_CODES.has(code)) return K_PETROLEUM;
    if (K_METALS_CODES.has(code)) return K_METALS;
    return K_GENERIC;
  }

  // P and U — unused commercial chemical products
  return PU_DISCARDED;
}
